import { useEffect, useState } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import Seo from '../../components/Seo';

export default function TermsPage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Terms & Conditions · F&B Insights"
        description="The terms and conditions that govern use of the F&B Insights platform and services."
        path="/terms"
      />
      <Navigation scrolled={scrolled} showMenuItems={false} />

      {/* Header band */}
      <header className="bg-fb-navy text-white">
        <div className="mx-auto max-w-[820px] px-8 py-14">
          <div className="mb-4 text-[11px] font-semibold uppercase tracking-[2px] text-fb-cyan">
            Legal
          </div>
          <h1 className="mb-3 text-[42px] font-bold tracking-[-1px]">Terms &amp; Conditions</h1>
        </div>
      </header>

      <div className="pb-20 pt-14">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="prose prose-lg max-w-none text-gray-700">
            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">Terms of Service</h4>

            <p className="mb-4">
              Welcome to F&B Insights. These Terms of Service contain the terms and conditions that
              govern all use of our Platform (as defined below) and Services (as defined below) and
              all content, services and/or products available on or through the Platform
              (collectively, the "<strong>F&B Insights Services</strong>").
            </p>

            <p className="mb-4">
              The F&B Insights Services are offered to you subject to your acceptance, without
              modification (other than Special Terms (as defined below) agreed by the parties
              pursuant to these Terms of Service), of all of the terms and conditions contained
              herein and all other operating rules, policies (including, without limitation, our
              Privacy Policy at{' '}
              <a
                href="https://fbinsights.ai/privacy-policy"
                className="text-blue-600 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                fbinsights.ai/privacy-policy
              </a>
              ), the Guidelines (as defined below) and any future modifications thereof, and
              procedures that may be published from time to time on the Platform or made available
              to you on or through the F&B Insights Services (collectively, the "
              <strong>Terms</strong>"). When accepted by you, these Terms form a legally binding
              contract between you and F&B Insights (as defined below). If you are entering into
              these Terms on behalf of an entity, such as your employer or the company you work for,
              you represent that you have the legal authority to bind that entity.
            </p>

            <p className="mb-4">
              PLEASE READ THESE TERMS CAREFULLY. BY REGISTERING FOR, ACCESSING, BROWSING, AND/OR
              OTHERWISE USING THE F&B INSIGHTS SERVICES, YOU ACKNOWLEDGE THAT YOU HAVE READ,
              UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS. IF YOU DO NOT AGREE TO BEBOUND BY
              THESE TERMS, DO NOT ACCESS, BROWSE OR OTHERWISE USE THE PLATFORM OR THE F&B INSIGHTS
              SERVICES.
            </p>

            <p className="mb-4">
              F&B Insights may, in its sole discretion, elect to suspend or terminate access to, or
              use of the F&B Insights Services to anyone who violates these Terms. If, applicable,
              you register for a free trial of the F&B Insights Services, the applicable provisions
              of these Terms will govern that free trial.
            </p>

            <p className="mb-6">
              The original language of these Terms is English. F&B Insights may make available
              translations for convenience. In case of conflicts between the original English
              version and any translation, the English version shall prevail.
            </p>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">Definitions</h4>

            <div className="overflow-x-auto mb-6">
              <table className="min-w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-4 py-2 text-left font-bold">Term</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-bold">
                      Definition
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Account</td>
                    <td className="border border-gray-300 px-4 py-2">
                      the primary means for accessing and using the F&B Insights Services;
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Authorization</td>
                    <td className="border border-gray-300 px-4 py-2">
                      any set of rights and privileges on F&B Insights website;
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Beta Service</td>
                    <td className="border border-gray-300 px-4 py-2">
                      a feature or functionality of F&B Insights Services that is in development or
                      has not been released as a final product which F&B Insights has made available
                      to Client for testing and evaluation;
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Client</td>
                    <td className="border border-gray-300 px-4 py-2">
                      a natural or legal person who has accepted these Terms with the F&B Insights,
                      which may include a Data Buyer, Retailer or Industry Member;
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Client Data</td>
                    <td className="border border-gray-300 px-4 py-2">
                      files and any other digital data and information, which is subjected to the
                      F&B Insights Services or otherwise inserted to the Platform;
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Content</td>
                    <td className="border border-gray-300 px-4 py-2">
                      any data and information available through F&B Insights Services or contained
                      within the Platform;
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Data</td>
                    <td className="border border-gray-300 px-4 py-2">
                      the information provided by a Retailer to F&B Insights, which F&B Insights
                      will sell to a Client or Data Buyer;
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Data Buyer</td>
                    <td className="border border-gray-300 px-4 py-2">
                      the Client who purchases the Data from F&B Insights;
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Fee</td>
                    <td className="border border-gray-300 px-4 py-2">payment to F&B Insights;</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Files</td>
                    <td className="border border-gray-300 px-4 py-2">
                      documents of any kind (images, spreadsheets, text files, etc.) that are
                      inserted to F&B Insights;
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Free Trial</td>
                    <td className="border border-gray-300 px-4 py-2">
                      temporary access for the purposes of trying out the Web Site and F&B Insights
                      Services without paying a Fee;
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Guidelines</td>
                    <td className="border border-gray-300 px-4 py-2">
                      additional guidelines or rules applicable to specific Features, applications,
                      products, or services which may be posted from time to time on the Platform or
                      otherwise made available on or through the F&B Insights Services;
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Fair Market Value ("FMV")</td>
                    <td className="border border-gray-300 px-4 py-2">
                      the price the Data would sell for on the open market assuming that both buyer
                      and seller are reasonably knowledgeable about the asset, free from undue
                      pressure.
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">F&B Insights, LLC</td>
                    <td className="border border-gray-300 px-4 py-2">
                      F&B Insights, LLC, a private limited liability company organizer under the
                      laws of the State of Florida;
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">F&B Insights Materials</td>
                    <td className="border border-gray-300 px-4 py-2">
                      the visual interfaces, graphics, design, systems, methods, information,
                      computer code, software, services, "look and feel", organization, compilation
                      of the content, code, data, including menu data, and all other elements of the
                      F&B Insights Services;
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">F&B Insights Services</td>
                    <td className="border border-gray-300 px-4 py-2">
                      the Web Site, System, Content, Platform and all content, services and/or
                      products available on or through the Platform;
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Industry Member</td>
                    <td className="border border-gray-300 px-4 py-2">
                      Includes any person engaged in business as a distiller, brewer, rectifier,
                      blender, or other producer, or an importer or wholesaler, of distilled
                      spirits, wine, or malt beverages, or as a bottler, or warehouseman and
                      bottler, of distilled spirits;
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Platform</td>
                    <td className="border border-gray-300 px-4 py-2">
                      the F&B Insights user experience, which includes but is not limited to, access
                      to offering for sale, selling and buying Data;
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Retailer</td>
                    <td className="border border-gray-300 px-4 py-2">
                      an entity that holds an alcohol beverage retail license, to sell alcoholic
                      beverages for on-premise or off-premise consumption in any state;
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Services</td>
                    <td className="border border-gray-300 px-4 py-2">
                      the offerings of F&B Insights on the Platform;
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Sensitive Information</td>
                    <td className="border border-gray-300 px-4 py-2">
                      means credit or debit card numbers; financial account numbers or wire
                      instructions; government-issued identification numbers (such as Social
                      Security numbers, passport numbers), biometric information, personal health
                      information (or other information protected under any applicable health data
                      protection laws), personal information of children protected under any child
                      data protection laws, and any other information or combinations of information
                      that falls within the definition of "special categories of data" under GDPR or
                      any other applicable law relating to privacy and data protection;
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Special Terms</td>
                    <td className="border border-gray-300 px-4 py-2">
                      any particulars, specifications and conditions by which the parties have
                      agreed to deviate from these Terms;
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">User</td>
                    <td className="border border-gray-300 px-4 py-2">
                      the authorized individual using F&B Insights;
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Web Site</td>
                    <td className="border border-gray-300 px-4 py-2">
                      the compilation of all web documents (including images, php and html files)
                      made available via ww.fbinsights.com or its sub domains or domains with
                      identical names under other top domains and owned by F&B Insights.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h5 className="text-xl font-bold text-fb-navy mt-8 mb-4">
              Authority to Enter into These Terms with F&B Insights
            </h5>

            <p className="mb-4">
              The use of the F&B Insights Services is subject to acceptance of these Terms. To
              accept these Terms for itself or on behalf of a Client, a person must have the legal
              capacity to do so. In the case of an individual, the individual must be at least 18
              years of age or have valid authorization from his/her legal representative or
              custodian. In the case of a legal entity, the individual signing must be authorized to
              sign on behalf of the legal entity and the entity must be duly incorporated and in
              good standing.
            </p>

            <p className="mb-4">
              The Terms are accepted as soon as one of the following occurs first:
            </p>

            <ol className="list-[lower-alpha] ml-8 mb-4">
              <li className="mb-2">
                the person has received the confirmation of the creation of the Account and
                necessary credentials from F&B Insights in order to log in to his/her/its Account;
                or
              </li>
              <li className="mb-2">
                for those F&B Insights Services and parts of the Web Site the use of which is not
                dependent on creating an Account, upon the moment of gaining access to such
                services.
              </li>
            </ol>

            <p className="mb-4">
              You may not, without F&B Insights' prior written consent, access the F&B Insights
              Services (i) for production purposes, (ii) if you are a competitor of F&B Insights,
              (iii) to monitor the availability, performance or functionality of the F&B Insights
              Services or (iv) for other benchmarking or competitive purposes. Once accepted, these
              Terms remain effective until terminated as provided for herein.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-8 mb-4">Modifications to Terms</h5>

            <p className="mb-4">
              F&B Insights reserves the right, at its sole discretion, to change, modify, add, or
              remove portions of the Terms at any time by posting such changes on or through the
              Platform or the F&B Insights Services. Please check these Terms periodically for
              changes. Your continued use of the F&B Insights Services after such changes have been
              posted as provided above constitutes your binding acceptance of such changes. Such
              amended Terms will automatically be effective upon the earlier of (i) your continued
              use of the F&B Insights Services, or (ii) 30 days from posting of such modified Terms
              on or through the Platform. Notwithstanding the foregoing, the resolution of any
              dispute that arises between you and F&B Insights will be governed by the Terms in
              effect at the time such dispute arose.
            </p>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">Our Responsibilities</h4>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">
              Provision of F&B Insights Services
            </h5>

            <p className="mb-4">
              F&B Insights will (a) make the F&B Insights Services, Content and Client Data
              available to a Client pursuant to these Terms, (b) provide applicable standard support
              for the F&B Insights Services to Client at no additional charge, and/or upgraded
              support (for an additional charge, if applicable), (c) use commercially reasonable
              efforts to make the F&B Insights Services available 24 hours a day, 7 days a week,
              except for:
            </p>

            <p className="mb-4">
              (i) planned downtime (of which F&B Insights shall give advance electronic notice as
              provided in the Guidelines), and (ii) any unavailability caused by circumstances
              beyond F&B Insights' reasonable control, including, for example, an act of God, act of
              government, flood, fire, earthquake, civil unrest, act of terror, strike or other
              labor problem, Internet service provider failure or delay, or denial of service
              attack.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Protection of Client Data</h5>

            <p className="mb-4">
              F&B Insights will maintain administrative, physical, and technical safeguards for
              protection of the security, confidentiality and integrity of Client Data, as described
              in the Guidelines. Those safeguards will include, but will not be limited to, measures
              for preventing access, use, modification or disclosure of Client Data by F&B Insights
              personnel except (a) to provide the F&B Insights Services and prevent or address
              service or technical problems, (b) as compelled by law in accordance with Section 7.4
              (Compelled Disclosure) below, or (c) as a Client or User expressly permit in writing.
            </p>

            <p className="mb-4">
              F&B Insights may use service providers to perform the Services. F&B Insights will make
              commercially reasonable efforts to ensure that data transfers to service providers
              meet requirements applicable to Clients' processing of data and will provide
              information on such data transfers in these Terms for Client's consideration.
            </p>

            <p className="mb-4">
              By agreeing to these Terms, the Client authorizes the F&B Insights (a general written
              authorization in the meaning of Article 28 (2) of Regulation (EU) 2016/679) to engage
              the following service providers for the purposes of performing the Services. If
              required by law, F&B Insights shall provide 10 business days advance notice before
              engaging any new service providers under the F&B Insights' general authorization.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">
              List of service providers processing Client Data
            </h5>

            <div className="overflow-x-auto mb-6">
              <table className="min-w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-4 py-2 text-left font-bold">
                      Processor
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-bold">
                      Entity country
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-bold">
                      Transfer mechanism
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-bold">
                      Applicable Service
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-bold">
                      Type of personal data
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Amazon Web Services, Inc</td>
                    <td className="border border-gray-300 px-4 py-2">USA / Europe</td>
                    <td className="border border-gray-300 px-4 py-2">
                      DPA + SCCs Module 3 for Client Data
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      Hosting and CDN services in Europe and the USA, depending on Client location
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      All Client Data stored by Clients as defined in our ToS
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Cloudflare, Inc.</td>
                    <td className="border border-gray-300 px-4 py-2">USA / Europe</td>
                    <td className="border border-gray-300 px-4 py-2">
                      DPA + SCCs Module 3 for Client Data
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      Content distribution, security, abuse prevention, and DNS services for web
                      traffic transmitted to and from the Services. This allows F&B Insights to
                      efficiently manage traffic and secure the Services
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      End-User or Agent IP address
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Google</td>
                    <td className="border border-gray-300 px-4 py-2">USA / Europe</td>
                    <td className="border border-gray-300 px-4 py-2">
                      DPA + SCCs Module 3 for Client Data
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      Hosting and CDN services in Europe and the USA, depending on Client location.
                      Machine learning processing. Google Places API
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      All Client Data stored by Clients as defined in our ToS
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Microsoft</td>
                    <td className="border border-gray-300 px-4 py-2">USA / Europe</td>
                    <td className="border border-gray-300 px-4 py-2">
                      DPA + SCCs Module 3 for Client Data
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      PowerBI Analytics, Data structuring
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      All Client Data stored by Clients as defined in our ToS
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">
              Using the F&B Insights Services
            </h4>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Establishing an Account</h5>

            <p className="mb-4">
              Certain features, functions, parts or elements of the F&B Insights Services can be
              used or accessed only by holders of an Account. The person who wishes to create an
              Account must:
            </p>

            <ol className="list-[lower-alpha] ml-8 mb-4">
              <li className="mb-2">complete the sign-up form on the Web Site; and</li>
              <li className="mb-2">accept these Terms.</li>
            </ol>

            <p className="mb-4">
              Each Client may have only one Account. If several persons need to use an Account on
              behalf of Client, Client must designate such persons as Users. Each such User shall be
              subject to the restrictions set forth in these Terms.
            </p>

            <p className="mb-4">
              If Client has designated Users and granted them Authorization, such Users will be
              deemed to be authorized to act on behalf of Client when using the Account. F&B
              Insights is not responsible for and shall have no liability for verifying the validity
              of Authorization of any User. However, F&B Insights may, in its discretion, request
              additional information or proof of the person's credentials. If F&B Insights is not
              certain if a User has been granted Authorization, F&B Insights may, in its sole
              discretion, prevent such User from accessing the F&B Insights Services.
            </p>

            <p className="mb-4">
              A User may be associated with multiple Clients and Accounts. Deleting a User from one
              Account will not remove the User from the Platform if he/she is connected to multiple
              Accounts.
            </p>

            <p className="mb-4">
              The Client and any User associated with an Account must provide F&B Insights with
              true, accurate, current, and complete information about the Client, Users or Account
              and keep it up to date.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Logging Into an Account</h5>

            <p className="mb-4">
              F&B Insights shall provide Client with a username and password ("Login Credentials")
              to be used to log in to its Account unless the Client uses the single sign-on Feature
              or another service to log in. These Login Credentials must not be used by multiple
              persons. If Client has designated several Users, each User will be provided with
              separate Login Credentials. Client and each User are responsible for keeping
              confidential all login credentials associated with an Account. Client must promptly
              notify F&B Insights:
            </p>

            <ol className="list-[lower-alpha] ml-8 mb-4">
              <li className="mb-2">
                of any disclosure, loss or unauthorized use of any Login Credentials;
              </li>
              <li className="mb-2">of a User's departure from the Client's organization;</li>
              <li className="mb-2">of a change in a User's role in the Client's organization;</li>
              <li className="mb-2">of any termination of a User's right for any reason.</li>
            </ol>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Termination of Account</h5>

            <p className="mb-4">
              Client may terminate these Terms at any time as provided in Section 17.
            </p>

            <p className="mb-4">
              F&B Insights shall permanently delete the Account within six months of the effective
              date of the termination.
            </p>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">Fees</h4>

            <p className="mb-4">
              Fees are non-refundable, i.e., there are no refunds or credits. The fee will depend on
              an accepted offer by a Data Buyer. F&B Insights will accept the amount due and pay the
              applicable party related payments, less a fee retained by F&B Insights.
            </p>

            <p className="mb-4">
              All fees are exclusive of all taxes, levies or duties applicable under any applicable
              law, unless stated otherwise stated herein. Client is solely responsible for the
              payment of such taxes, levies or duties.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Payment Card Authorization</h5>

            <p className="mb-4">
              F&B Insights may seek pre-authorization of Client's payment card account prior to your
              purchase of F&B Insights Services in order to verify that the card is valid and has
              the necessary funds or credit available to cover your purchase. You authorize such
              payment card account to pay any amounts described herein, and authorize F&B Insights
              to charge all sums described in these Terms to such card account. You agree to provide
              F&B Insights updated information regarding your payment card account upon F&B
              Insights' request and any time the information earlier provided is no longer valid.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Direct Debit Payments</h5>

            <p className="mb-4">
              In some markets F&B Insights may, if Client elects, request that Client complete a
              direct debit mandate to enable direct debit payments. In such cases F&B Insights shall
              comply with all applicable national rules and regulations related to direct debit
              payments. We use Stripe and Plaid to process your Credit Card and Direct Debit
              payments.
            </p>

            <p className="mb-4">
              More information on how Stripe and Plaid processes your personal data and your data
              protection rights, including your right to object, is available at:
            </p>

            <ul className="list-disc ml-8 mb-4">
              <li className="mb-2">
                <a href="https://stripe.com/legal/ssa" className="text-blue-600 hover:underline">
                  https://stripe.com/legal/ssa
                </a>
              </li>
              <li className="mb-2">
                <a href="https://plaid.com/legal/" className="text-blue-600 hover:underline">
                  https://plaid.com/legal/
                </a>
              </li>
            </ul>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Electronic Invoice</h5>

            <p className="mb-4">
              If F&B Insights has not sought pre-authorization of your payment card, then before the
              end of each payment interval, Client will be issued an electronic invoice for payment
              of the Fee of the next payment interval. Client must pay the invoice by the due date
              indicated on the invoice.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Overage Charges</h5>

            <p className="mb-4">
              Upon delay with any payments, F&B Insights may require the Client to pay interest on
              the delay (penalty for late payment) for the period as of the time the payment
              obligation falls due until conforming performance is rendered. The interest rate of
              penalty for late payment due shall be 1% per month; provided, however, if any interest
              paid to F&B Insights is determined to be in excess of the then legal maximum rate,
              then that portion of each interest payment representing an amount in excess of the
              then legal maximum rate shall be deemed a payment of amounts owing under Section 6.2.
              Upon calculation of the penalty for late payment, one year shall be deemed to contain
              365 calendar days.
            </p>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">Client data</h4>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">
              Uploading Client Data to Platform
            </h5>

            <p className="mb-4">
              If the Client uploads Client Data to the Platform, such Client Data and any processing
              of such Client Data must be in compliance with these Terms and applicable law. All
              rights, title and interest in and to the Client Data belong to the Client or third
              persons (including Users, persons and Organizations) whether posted and/or uploaded by
              you or made available on or through the F&B Insights Services by F&B Insights. By
              uploading Client Data to the Platform, Client authorizes F&B Insights to process the
              Client Data. The Client is responsible for ensuring that:
            </p>

            <ol className="list-[lower-alpha] ml-8 mb-4">
              <li className="mb-2">
                the Client and any of the Users associated with the Account do not create, transmit,
                display or make otherwise available any Client Data that violates the terms of these
                Terms, the rights of F&B Insights, other Clients or Users, persons or Organizations
                or is harmful (for example viruses, worms, malware and other destructive codes),
                offensive, threatening, abusive, harassing, tortuous, defamatory, vulgar, obscene,
                invasive of another's privacy, hateful or otherwise unlawful; and
              </li>
              <li className="mb-2">
                the Client and all of the Users associated with the Account have the necessary
                rights to use the Client Data, including to insert it into the Platform and process
                it by means of the Account.
              </li>
            </ol>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">No Guarantee of Accuracy</h5>

            <p className="mb-4">
              F&B Insights does not guarantee any accuracy with respect to any information contained
              in any Client Data, and strongly recommends that you think carefully about what you
              transmit, submit or post to or through the F&B Insights Services. You understand that
              all information contained in Client Data is the sole responsibility of the person from
              whom such Client Data originated. This means that Client, and not F&B Insights, is
              entirely responsible for all Client Data that is uploaded, posted, transmitted, or
              otherwise made available through the F&B Insights Services, as well as for any actions
              taken by the F&B Insights or other Clients or Users as a result of such Client Data.
              F&B Insights is relying upon the Retailer and its accuracy but F&B Insights is not
              responsible for the accuracy or completeness of any Data.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">
              Sensitive Information and Unlawful Client Data
            </h5>

            <p className="mb-4">
              You will not use the F&B Insights Service in any way that violates these Terms, to
              process Sensitive Information, or Client Data that in any manner is prohibited by law.
            </p>

            <p className="mb-4">
              F&B Insights is not obliged to pre-screen, monitor or filter any Client Data or its
              processing by the Client in order to determine if it is Sensitive Information or
              unlawful in nature. However, if the F&B Insights has reason to believe that the Client
              is processing unlawful Client Data or Sensitive Information, or the action of its
              processing is unlawful by in nature, F&B Insights has the right to:
            </p>

            <ol className="list-decimal ml-8 mb-4">
              <li className="mb-2">
                notify the Client of such unlawful Client Data or sensitive Information;
              </li>
              <li className="mb-2">deny its publication in the F&B Insights Services;</li>
              <li className="mb-2">
                demand that the Client bring the unlawful Client Data into compliance with these
                Terms and applicable law;
              </li>
              <li className="mb-2">
                temporarily or permanently remove the unlawful Client Data, or Sensitive Information
                from the F&B Insights Services, restrict access to it or delete it.
              </li>
            </ol>

            <p className="mb-4">
              If F&B Insights is presented convincing evidence that the Client Data is not unlawful
              or Sensitive Information, F&B Insights may, at its sole discretion, restore such
              Client Data or Sensitive Information, which was removed from the F&B Insights
              Services, Account or access to which was restricted.
            </p>

            <p className="mb-4">
              In addition, in the event the F&B Insights believes in its sole discretion Client Data
              violates applicable laws, rules or regulations or these Terms, or is Sensitive
              Information, the F&B Insights may (but has no obligation), to remove such Client Data
              at any time with or without notice.
            </p>

            <p className="mb-4">
              Without limiting the generality of the preceding sentence, F&B Insights Inc complies
              with the Digital Millennium Copyright Act and will remove Client Data from the
              Platform upon receipt of a compliant takedown notice.
            </p>

            <p className="mb-4">
              The F&B Insights Service is not designed to comply with industry-specific regulations
              such as the Health Insurance Portability and Accountability Act (HIPAA) or the Federal
              Information Security Management Act (FISMA), so you may not use the F&B Insights
              Services where your communications would be subject to such laws. You may not use the
              F&B Insights Service in a way that would violate the Gramm-Leach-Bliley Act (GLBA).
              Nothing contained in this section limits the usage restrictions specific to Sensitive
              Information under the Terms.
            </p>

            <p className="mb-4">
              No Sensitive Information. YOU ACKNOWLEDGE THAT THE F&B INSIGHTS SERVICES HAVE NOT BEEN
              DESIGNED TO PROCESS OR MANAGE SENSITIVE INFORMATION AND ACCORDINGLY YOU AGREE NOT TO
              USE THE F&B INSIGHTS SERVICES TO COLLECT, MANAGE OR PROCESS SENSITIVE INFORMATION. WE
              WILL NOT HAVE AND WE SPECIFICALLY DISCLAIM ANY LIABILITY THAT MAY RESULT FROM YOUR USE
              OF THE SUBSCRIPTION SERVICE TO COLLECT, PROCESS OR MANAGE SENSITIVE INFORMATION.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Compelled Disclosure</h5>

            <p className="mb-4">
              F&B Insights may disclose a Client's confidential information to the extent compelled
              by law to do so. In such instance, F&B Insights will use commercially reasonable
              efforts to provide Client with prior notice of the compelled disclosure (to the extent
              legally permitted) and Client shall provide reasonable assistance, at its cost, if
              Client wishes to contest the disclosure. If F&B Insights is compelled by law to
              disclose Client's confidential information as part of a civil proceeding to which F&B
              Insights is a party, and Client is not contesting the disclosure, Client will
              reimburse F&B Insights for its reasonable cost of compiling and providing secure
              access to that confidential information.
            </p>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">Services</h4>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">
              Use of the F&B Insights Services
            </h5>

            <p className="mb-4">
              Subject to these Terms, and the payment of the applicable service Fee, F&B Insights
              grants Client and its authorized users a non-exclusive, non-transferable,
              non-sub-licensable license to use the F&B Insights Services to:
            </p>

            <ol className="list-[lower-alpha] ml-8 mb-4">
              <li className="mb-2">
                collect, store and organize Client Data, such as add new Persons and Organizations,
                create Pipelines and add Stages into these Pipelines, generate reports based on
                Client Data, add Deals into Stages and monitor their evolvement through the next
                Stages, add Products to be offered for sale to Persons and Organizations; add new
                Users and grant them Authorizations, assign Activities to a particular User;
              </li>
              <li className="mb-2">modify and delete Client Data;</li>
              <li className="mb-2">
                customize the standard Features or functionality of the F&B Insights Services;
              </li>
              <li className="mb-2">
                receive reasonable help and guidance and from F&B Insights regarding the use of the
                F&B Insights Services.
              </li>
            </ol>

            <p className="mb-4">
              If F&B Insights determines Client usage of the Services to be outside of the permitted
              and intended use as outlined herein, or bandwidth usage of the Service or any Features
              or functionality to be significantly excessive in relation to other Users, F&B
              Insights reserves the right to suspend respective Client Account, (or part thereof)
              until Client assures F&B Insights that Client shall refrain from further abuse of the
              Services.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Technical Support</h5>

            <p className="mb-4">
              F&B Insights shall provide reasonable technical support to Client and its authorized
              User at the reasonable request of the Client. F&B Insights shall respond to enquiries
              of support from a Client utilizing the contacts set forth below as soon as reasonably
              possible. Responding to enquiries of Clients and Users who have accepted these Terms
              but do not have an Account may be less expedient or may not occur at all.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Modifications to Service</h5>

            <p className="mb-4">
              F&B Insights reserves the right to modify the F&B Insights Services or any part or
              element thereof from time to time without prior notice, including, without limitation:
            </p>

            <ol className="list-[lower-alpha] ml-8 mb-4">
              <li className="mb-2">rebranding the F&B Insights Services at its sole discretion;</li>
              <li className="mb-2">
                ceasing providing or discontinuing the development any particular F&B Insights
                Service or part or element of the Platform temporarily or permanently;
              </li>
              <li className="mb-2">
                taking such action as is necessary to preserve F&B Insights' rights upon any use of
                the F&B Insights Services that may be reasonably interpreted as violation of F&B
                Insights' intellectual property rights, distribution of Internet viruses, worms,
                Trojan horses, malware, and other destructive activities or illegal activity.
              </li>
            </ol>

            <p className="mb-4">
              As applicable, Client may be notified of such modifications when logging in to the
              Account. Modifications, including change in applicable rates for the F&B Insights
              Services, will become effective thirty (30) days before the effective date of such
              modification.
            </p>

            <p className="mb-4">
              If the Client does not accept the modification, the Client shall notify F&B Insights
              the effective date of the modification, and these Terms will terminate on the
              effective date of the modification. The Client's continued use of the F&B Insights
              Services, or any part or element thereof, after the effective date of a modification
              shall indicate its consent to the modifications. F&B Insights shall not be liable to
              the Client or to any third party for any modification, suspension or discontinuance of
              the F&B Insights Services, or any part or element thereof.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Additional Features</h5>

            <p className="mb-4">
              F&B Insights may from time to time make additional Features available through the F&B
              Insights Services, which may be subject to Additional Terms. The Client's use of any
              such Feature is subject to their acceptance of Additional Terms presented within the
              relevant Feature or these Terms of Service.
            </p>

            <p className="mb-4">
              Google Maps processes address data into Google Maps links. Users are bound by the{' '}
              <a
                href="https://maps.google.com/help/terms_maps.html"
                target="_blank"
                rel="noopener"
                className="text-blue-600 hover:underline"
              >
                Google Maps/Google Earth Additional Terms of Service
              </a>{' '}
              (including the{' '}
              <a
                href="https://www.google.com/intl/ALL/policies/privacy/index.html"
                target="_blank"
                rel="noopener"
                className="text-blue-600 hover:underline"
              >
                Google Privacy Policy
              </a>
              ). Emails sent to F&B Insights are processed by Gmail in accordance with the{' '}
              <a
                href="https://policies.google.com/terms?hl=en-US"
                target="_blank"
                rel="noopener"
                className="text-blue-600 hover:underline"
              >
                Google Terms of Service
              </a>
              .
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Beta Services</h5>

            <p className="mb-4">
              F&B Insights may offer certain features of functionality for the purpose of testing
              and evaluation ("Beta Service" or "Beta Services"). The F&B Insights reserves the
              right to fully or partially discontinue, at any time and from time to time,
              temporarily or permanently, any of the Beta Services with or without notice to the
              Client. The Client agrees that the F&B Insights will not be liable to the Client or to
              any third party for any harm related to, arising out of the Client's use of the Beta
              Services, or caused by the modification, suspension or discontinuance of any of the
              Beta Services for any reason.
            </p>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">Data Processing Contract</h4>

            <p className="mb-4">
              For the purposes of Article 28 of Regulation (EU) 2016/679, these Terms constitute the
              data processing contract between the Client as the data controller and the F&B
              Insights as the data processor. The Client hereby instructs the F&B Insights to
              process the data as described in these Terms.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">
              Subject matter and nature of processing
            </h5>

            <p className="mb-4">
              The F&B Insights provides the Platform where the Client, as the data controller, can
              collect, store and organize the personal data of data subjects determined by the
              Client.
            </p>

            <p className="mb-4">
              The Platform has been designed to work as a sales CRM tool but, to the extent not
              regulated by these Terms, the Client decides how they use the Platform.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Duration</h5>

            <p className="mb-4">
              The F&B Insights will process data on behalf of the Client until the termination of
              the F&B Insights Services in accordance with these Terms. Upon termination and
              assuming the Client has had a paid Account, the F&B Insights will store the Client's
              data for a period of six months, should the Client wish to reopen the Account to
              resume the use of the F&B Insights Services or to export Client Data, unless
              instructed otherwise by the Client. The content of closed Free Trial Accounts, if
              applicable, are deleted within 30 days of the date of closure. After the
              aforementioned periods, Contents of the Accounts may still be kept as part of the F&B
              Insights' technical server archival backups for an additional three months. The F&B
              Insights deletes or returns all the personal data to the controller after the end of
              the provision of services relating to processing and deletes existing copies unless
              Union or Member State law requires storage of the personal data.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">
              Parties' rights and obligations
            </h5>

            <p className="mb-4">
              The Client's rights and obligations regarding Client Data are provided in sections 4
              through 10 of these Terms. The F&B Insights ensures that persons authorized to process
              the personal data have committed themselves to confidentiality or are under an
              appropriate statutory obligation of confidentiality.
            </p>

            <p className="mb-4">
              The F&B Insights takes all measures required pursuant to Article 32 of Regulation (EU)
              2016/679. The F&B Insights undertakes to make available to the controller all
              information necessary to demonstrate compliance with their obligations and to allow
              for and contribute to audits, including inspections, conducted or mandated by the
              Client as the data controller.
            </p>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">Restrictions</h4>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Prohibited Activities</h5>

            <p className="mb-4">
              Client and its authorized Users may use the F&B Insights Services and any part or
              element thereof only in the scope, with the means and for purposes as identified in
              these Terms and applicable law. By way of example, neither the Client nor any User
              may:
            </p>

            <ol className="list-[lower-alpha] ml-8 mb-4">
              <li className="mb-2">
                use the F&B Insights Services or any part or element thereof to commit a crime,
                breach any applicable law, including applicable alcohol beverage control laws,
                regulations, advisories or opinions, or entice or invite others to carry out such
                actions;
              </li>
              <li className="mb-2">
                copy, duplicate, distribute, modify, adapt, hack, create derivative works, provide,
                disclose, divulge, exploit or otherwise grant Access or make the F&B Insights
                Services available in whole or in part to any third persons, unless such third
                person is another authorized User of the same Client;
              </li>
              <li className="mb-2">
                use the F&B Insights Services or any part or element thereof unless it has agreed to
                these Terms.
              </li>
            </ol>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">
              Certain Uses Require F&B Insights Consent
            </h5>

            <p className="mb-4">
              The Client or any User may not, without F&B Insights' prior express written consent
              (e-mail, fax, Skype, etc.):
            </p>

            <ol className="list-[lower-alpha] ml-8 mb-4">
              <li className="mb-2">
                sell, resell, lease, license, sublicense, distribute, provide, disclose, divulge,
                exploit or otherwise grant Access or make the F&B Insights Services available in
                whole or in part to any third persons, unless such third person is another
                authorized User of the same Client;
              </li>
              <li className="mb-2">
                use the F&B Insights Services or any part or element thereof in a scope, with means
                or for purposes other than those for which their functionality was created;
              </li>
              <li className="mb-2">
                use the F&B Insights Services or any part or element thereof by means of programs
                that send them automatic enquiries or requests, unless such program has been made
                available by F&B Insights.
              </li>
            </ol>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Trade Control Compliance</h5>

            <p className="mb-4">
              The Client, any user or agent ("Third Party") hereby represents, warrants, covenants,
              and agrees that, with respect to the F&B Insights Services:
            </p>

            <ol className="list-[lower-alpha] ml-8 mb-4">
              <li className="mb-2">
                Third Party has complied and shall comply with, and shall cause its directors,
                officers, employees, and agents to comply with, U.S., EU, and any other applicable
                foreign economic, trade, and financial sanctions laws and regulations, including
                economic and trade sanctions administered by the U.S. Department of the Treasury's
                Office of Foreign Assets Control ("OFAC") and the Department of State (collectively,
                "Sanctions"), and U.S., EU and applicable foreign laws and regulations pertaining to
                export controls, including those administered by the U.S. Departments of Commerce
                and State (collectively, "Trade Controls").
              </li>
              <li className="mb-2">
                Third Party shall take no action, directly or indirectly, that would cause F&B
                Insights or any of its subsidiaries or affiliates, or any of their respective
                officers, directors, employees, or representatives, to violate any Trade Controls.
              </li>
              <li className="mb-2">
                Neither the Third Party nor any of its officers or directors, employees, and any
                agents or other representatives acting on their behalf (i) has been or is designated
                on any Sanctions- or export-related list of restricted or blocked persons, including
                designation on OFAC's List of Specially Designated Nationals and Blocked Persons
                (the "SDN List") or OFAC's Sectoral Sanctions Identifications List (the "SSI List"),
                (ii) is located in, organized under the laws of, or resident in any country or
                territory that is itself the subject of any economic or financial sanctions by any
                Governmental Authority, including, but not limited to, Cuba, Iran, Syria, North
                Korea, Venezuela and the Crimea Region of Ukraine, (iii) is or has been greater than
                50% owned or controlled by any Person or Persons described in clause (i) or (ii)
                (collectively with (i) and (ii), a "Restricted Person"), or (iv) has or will provide
                the F&B Insights Services to any Persons described in clauses (i)-(iii).
              </li>
              <li className="mb-2">
                Third Party will promptly notify F&B Insights if Third Party or any personnel
                employed by or affiliated with Third Party (i) commits any actual or potential
                breach of Trade Controls relation to the F&B Insights Services, or (ii) becomes a
                Restricted Person.
              </li>
              <li className="mb-2">
                F&B Insights shall have the right to immediately terminate the access to, or use of
                the F&B Insights Services without notice or liability to Third Party, if Third
                Party, or any person employed by or affiliated with Third Party takes any action in
                violation of the provisions described herein or if F&B Insights determines, in its
                sole discretion, that the Third Party's continued use of the F&B Insights Services
                could violate Trade Controls.
              </li>
            </ol>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">
              <a id="privacy-and-data-protection" className="cursor-pointer">
                Privacy and Data Protection
              </a>
            </h4>

            <p className="mb-4">
              Your use of the Service is also governed by our{' '}
              <a
                href="https://fbinsights.ai/privacy-policy"
                className="text-blue-600 hover:underline cursor-pointer"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
              , which is incorporated into these Terms by reference. By using the Service, you
              consent to the collection, use, and disclosure of your information as described in the
              Privacy Policy.
            </p>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">
              Intellectual Property Rights
            </h4>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">
              F&B Insights' Intellectual Property Rights in the F&B Insights Services
            </h5>

            <p className="mb-4">
              The F&B Insights Services, F&B Insights Materials, F&B Insights trade names and
              trademarks, and any parts or elements thereof are solely and exclusively owned and
              operated by F&B Insights and its third-party vendors and hosting partners. F&B
              Insights Materials are protected by copyright, trade dress, patent, trade secret, and
              trademark laws, international conventions and treaties, and all other relevant
              intellectual property and proprietary rights laws. F&B Insights, its affiliates and
              licensors retain all right, title and interest in such F&B Insights Services, F&B
              Insights Materials, F&B Insights trade names and trademarks, and any parts or
              elements. Your use of the F&B Insights Services and F&B Insights Materials, and any
              parts or elements does not grant to you any ownership rights by downloading any
              copyrighted material from or through the Platform or the F&B Insights Services. You
              shall not copy, distribute or publish any Content or any information obtained or
              derived therefrom except as permitted on or through the F&B Insights Services or as
              otherwise permitted by applicable law.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Client Data</h5>

            <ol className="list-[lower-alpha] ml-8 mb-4">
              <li className="mb-2">
                F&B Insights may use Client Data in an aggregated or anonymized format for research,
                analytics, educational and other similar purposes. F&B Insights may not otherwise
                use or display Client Data without Client's written consent. F&B Insights respects
                your right to exclusive ownership of your Client Data. Unless specifically permitted
                by you, your use of the F&B Insights Services does not grant F&B Insights the
                license to use, reproduce, adapt, modify, publish or distribute the Client Data
                created by you or stored in your Account for F&B Insights' commercial, marketing or
                any similar purpose. Client expressly grants F&B Insights the right to use and
                analyze aggregate system activity data associated with use of the F&B Insights
                Services by Client and its Users for the purposes of optimizing, improving or
                enhancing the way the F&B Insights Services operate, and to create new Features and
                functionality in connection with the F&B Insights Services in the sole discretion of
                F&B Insights.
              </li>
              <li className="mb-2">
                Client is solely responsible for its own Client Data and the consequences of posting
                or publishing them on or through the F&B Insights Service. In connection with Client
                Data, Client affirms, represents, and warrants that: (i) Client either owns its
                Client Data or has the necessary licenses, rights, consents, and permissions to use
                and authorize F&B Insights to display or otherwise use the Client Data under all
                patent, trademark, copyright, trade secret, moral right, privacy right, right of
                publicity, or any other intellectual property or proprietary right; and (ii) Client
                Data, F&B Insights' or any F&B Insights Licensee's use of such Client Data pursuant
                to these Terms, and F&B Insights' or any F&B Insights Licensee's exercise of the
                license rights set forth in Section 11.3(a), do not and will not: (a) infringe,
                violate, or misappropriate any third-party right, including any copyright,
                trademark, patent, trade secret, moral right, privacy right, right of publicity, or
                any other intellectual property or proprietary right; (b) violate any applicable law
                or regulation anywhere in the world; or (c) require obtaining a license from or
                paying any fees and/or royalties by F&B Insights to any third party for the
                performance of any F&B Insights Services Client has chosen to be performed by F&B
                Insights or for the exercise of any rights granted in these Terms, unless Client and
                F&B Insights otherwise agree.
              </li>
            </ol>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Feedback</h5>

            <p className="mb-4">
              If Client or a User provides F&B Insights with any comments, bug reports, feedback, or
              modifications for the F&B Insights Services ("Feedback"), F&B Insights shall have the
              right to use such Feedback at its discretion, including, but not limited to the
              incorporation of such suggested changes into the F&B Insights Services.
            </p>

            <p className="mb-4">
              Client or User (as applicable) hereby grants F&B Insights a perpetual, irrevocable,
              nonexclusive, royalty free license under all rights necessary to incorporate, publish,
              reproduce, distribute, modify, adapt, prepare derivative works of, publicly display,
              publicly perform, exploit and use your Feedback for any purpose. F&B Insights shall
              have the right to modify or remove any Feedback provided in the public areas of the
              Web Site if the F&B Insights deems, at its discretion, harmful, offensive,
              threatening, abusive, harassing, tortuous, defamatory, vulgar, obscene, invasive of
              another's privacy, hateful or otherwise unlawful.
            </p>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">
              Third-Party Sites, Products and Services
            </h4>

            <p className="mb-4">
              The F&B Insights Services may include links to other websites or services ("Linked
              Sites") solely as a convenience to Clients. Unless otherwise specifically and
              explicitly indicated, F&B Insights does not endorse any such Linked Sites or the
              information, material, products, or services contained on or accessible through Linked
              Sites. Furthermore, F&B Insights makes no express or implied warranties with regard to
              the information, material, products, or services that are contained on or accessible
              through Linked Sites.
            </p>

            <p className="mb-4">
              ACCESS AND USE OF LINKED SITES, INCLUDING THE INFORMATION, MATERIAL, PRODUCTS, AND
              SERVICES ON LINKED SITES OR AVAILABLE THROUGH LINKED SITES, IS SOLELY AT YOUR OWN
              RISK.
            </p>

            <p className="mb-4">
              Any content referred to as community provided is provided by third parties and not
              developed or maintained by F&B Insights. By using any community marked code or
              libraries in your software development, you acknowledge and agree that F&B Insights is
              not in any way responsible for the performance or damages caused by such community
              provided code or library.
            </p>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">Disclaimers; No Warranty</h4>

            <p className="mb-4">
              UNLESS OTHERWISE EXPRESSLY STATED BY SUPPLIER, THE F&B INSIGHTS SERVICES, F&B INSIGHTS
              MATERIAL, AND ANY CONTENT, SERVICES, OR FEATURES MADE AVAILABLE IN CONJUNCTION WITH OR
              THROUGH THE F&B INSIGHTS SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
              WARRANTIES OF ANY KIND EITHER EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMISSIBLE
              PURSUANT TO APPLICABLE LAW, SUPPLIER AND ITS AFFILIATES DISCLAIM ALL WARRANTIES,
              STATUTORY, EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT OF PROPRIETARY
              RIGHTS, CORRECTNESS, ACCURACY, AND RELIABILITY.
            </p>

            <p className="mb-4">
              UNLESS OTHERWISE EXPRESSLY STATED BY SUPPLIER, SUPPLIER AND ITS AFFILIATES DO NOT
              WARRANT THAT THE F&B INSIGHTS SERVICES AND ANY CONTENT, CLIENT DATA SERVICES, OR
              FEATURES MADE AVAILABLE IN CONJUNCTION WITH OR THROUGH THE F&B INSIGHTS SERVICES WILL
              BE UNINTERRUPTED OR ERROR-FREE, THAT DEFECTS WILL BE CORRECTED, OR THAT THE F&B
              INSIGHTS SERVICES AND ANY CONTENT, CLIENT DATA, SERVICES, OR FEATURES MADE AVAILABLE
              IN CONJUNCTION WITH OR THROUGH THE F&B INSIGHTS SERVICES OR THE SERVER THAT MAKES THEM
              AVAILABLE ARE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
            </p>

            <p className="mb-4">
              UNLESS OTHERWISE EXPRESSLY STATED BY SUPPLIER, SUPPLIER AND ITS AFFILIATES DO NOT
              WARRANT OR MAKE ANY REPRESENTATIONS REGARDING THE USE OR THE RESULTS OF THE USE OF THE
              PLATFORM, THE F&B INSIGHTS SERVICES, F&B INSIGHTS MATERIAL OR ANY LINKED SITES, IN
              TERMS OF CORRECTNESS, ACCURACY, RELIABILITY, COMPLIANCE WITH APPLICABLE LAWS, OR
              OTHERWISE.
            </p>

            <p className="mb-4">
              THE LAWS OF CERTAIN COUNTRIES AND STATES DO NOT ALLOW LIMITATIONS ON IMPLIED
              WARRANTIES OR THE EXCLUSION OR LIMITATION OF CERTAIN DAMAGES. IF THESE LAWS APPLY TO
              YOU, SOME OR ALL OF THE ABOVE DISCLAIMERS, EXCLUSIONS, OR LIMITATIONS MAY NOT APPLY TO
              YOU, AND YOU MIGHT HAVE ADDITIONAL RIGHTS.
            </p>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">Indemnification</h4>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">General Indemnification</h5>

            <p className="mb-4">
              You agree to defend, indemnify and hold harmless F&B Insights and its affiliates, and
              their respective directors, officers, employees and agents, from any claims, losses,
              damages, liabilities, including attorney's fees, arising out of your use or misuse of
              the F&B Insights Services, F&B Insights Materials, representations made to the F&B
              Insights, its affiliates and/or third parties, violation of these Terms, violation of
              the rights of any other person or entity, or any breach of the foregoing
              representations, warranties, and covenants. F&B Insights reserves the right, at its
              own expense, to assume the exclusive defense and control of any matter for which you
              are required to indemnify F&B Insights, and you agree to cooperate with such defense
              of these claims.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">
              Alcohol Beverage Compliance
            </h5>

            <p className="mb-4">
              F&B Insights understands and appreciates that various laws and regulations, including
              but not limited to alcohol beverage control laws, govern the Retailer and Industry
              Member. F&B Insights allows Data Buyers, who may be Industry Members, to purchase
              Data. F&B Insights will do its part to assist Clients with compliance, though
              compliance with applicable laws does ultimately rest with the Users.
            </p>

            <p className="mb-4">
              You may not use the F&B Insights Services in violation of the Federal Alcohol
              Administration Act of 1935, as amended, or any other federal, state or local laws and
              regulations, including but not limited to those administered by the Alcohol and
              Tobacco Tax and Trade Bureau ("TTB") or any state alcohol beverage control board.
            </p>

            <p className="mb-4">
              Data Buyers are purchasing Data only. All Users represent and warrant that: (i) it
              will not condition any purchase or agreement to F&B Insights may not be used to, in
              any way, request or influence a Retailer to purchase, sell or serve any particular
              alcoholic beverages; (ii) any fees paid or received, including for use of and through
              the F&B Insights Services, is not conditioned in any way upon any Retailer's purchase
              or agreement to purchase any alcohol beverage products produced, sold or offered for
              sale by any alcohol beverage Industry Member; and (iii) any funds received/paid or
              services provided/received is not and will not be used to induce any Retailer to
              purchase any of Industry Member's products to the exclusion of any competitive
              products. You agree to defend, indemnify and hold harmless F&B Insights and its
              affiliates, and their respective directors, officers, employees and agents, from any
              claims, losses, damages, liabilities, including attorney's fees, arising out of the
              use or misuse of the F&B Insights Services.
            </p>

            <p className="mb-4">
              F&B Insights represents and warrants that it holds no retail licensee for the sale of
              alcohol beverage products, and no officer, director, manager, owner, or employee of
              any retail licensee has any ownership interest in F&B Insights; neither F&B Insights,
              nor any officer, director, manager, owner, or employee of F&B Insights, own or have
              any direct or indirect interest in any Retailer, except for ordinary investments in
              publicly-traded companies.
            </p>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">Limitation of Liability</h4>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">No Liability</h5>

            <p className="mb-4">
              F&B Insights shall not be liable to the Client or User for any consequences resulting
              from:
            </p>

            <ol className="list-[lower-alpha] ml-8 mb-4">
              <li className="mb-2">any use of F&B Insights Services;</li>
              <li className="mb-2">
                any modifications in these Terms, calculation and rates of Fees, the F&B Insights
                Services, F&B Insights Material, or any part or element thereof (including but not
                limited to Account), including any error, permanent or temporary interruption,
                discontinuance, suspension or other type of unavailability of the F&B Insights
                Services or F&B Insights Material;
              </li>
              <li className="mb-2">
                deletion of, corruption of, or failure to store any Client Data;
              </li>
              <li className="mb-2">
                use of Client Data by the Client or any of the Users associated with the Account;
              </li>
              <li className="mb-2">upgrading or downgrading the current Plan;</li>
              <li className="mb-2">
                any disclosure, loss or unauthorized use of the login credentials of Client or any
                authorized User due to Client's failure to keep them confidential;
              </li>
              <li className="mb-2">
                the Client's use of the Account or the F&B Insights Services by means of browsers
                other than those accepted or supported by the F&B Insights;
              </li>
              <li className="mb-2">
                the application of any remedies against the Client or authorized Users by the F&B
                Insights, for example if the Client or User has committed a crime or conducted a
                breach of applicable law by using the F&B Insights Services or any part or element
                thereof;
              </li>
              <li className="mb-2">
                the differences between technologies and platforms used for access, for example if
                certain Features, functions, parts or elements of the F&B Insights Services are
                designed for use on a personal computer or laptop and do not function on a mobile
                platform or a tablet;
              </li>
              <li className="mb-2">
                the F&B Insights' application of the remedies described in these Terms, even if the
                reasonable grounds or legal basis for the application of these remedies turned out
                to be unfounded or invalid afterwards
              </li>
            </ol>

            <p className="mb-4">
              In addition, F&B Insights and its affiliates shall not be liable to the Client for any
              claim by any User, person, organization or third persons against the Client arising
              out of the Client's failure to:
            </p>

            <ol className="list-[lower-alpha] ml-8 mb-4">
              <li className="mb-2">
                provide F&B Insights with accurate information about the Client, Users or Account;
              </li>
              <li className="mb-2">
                notify F&B Insights of any reasons due to which a User does not have the right to
                use the Account on behalf of the Client;
              </li>
              <li className="mb-2">
                provide any Products which it has agreed to provide to such a person or organization
                (whether such failure arises as a result of F&B Insights' negligence, breach of
                these Terms or otherwise);
              </li>
              <li className="mb-2">ensure the lawfulness of the Client Data;</li>
              <li className="mb-2">obtain the necessary rights to use the Client Data;</li>
              <li className="mb-2">
                abide by any of the restrictions described in these Terms; or
              </li>
              <li className="mb-2">
                comply with any applicable laws, regulations, advisories, codes, or regulatory
                guidance
              </li>
            </ol>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Limitation of Liability</h5>

            <p className="mb-4">
              IN NO EVENT SHALL THE AGGREGATE LIABILITY OF F&B INSIGHTS AND ITS AFFILIATES ARISING
              OUT OF OR RELATED TO THESE TERMS EXCEED THE TOTAL AMOUNT PAID BY CLIENT HEREUNDER FOR
              THE F&B INSIGHTS SERVICES GIVING RISE TO THE LIABILITY IN THE SIX MONTHS PRECEDING THE
              FIRST INCIDENT OUT OF WHICH THE LIABILITY AROSE.
            </p>

            <p className="mb-4">
              THE FOREGOING LIMITATION WILL APPLY WHETHER AN ACTION IS IN CONTRACT OR TORT AND
              REGARDLESS OF THE THEORY OF LIABILITY BUT WILL NOT LIMIT CLIENT'S PAYMENT OBLIGATIONS
              UNDER THE "PAYMENT" SECTION ABOVE.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">
              Exclusion of Consequential and Related Damages
            </h5>

            <p className="mb-4">
              IN NO EVENT WILL EITHER PARTY OR ITS AFFILIATES HAVE ANY LIABILITY ARISING OUT OF OR
              RELATED TO THESE TERMS FOR ANY LOST PROFITS, LOSS OF DATA, REVENUES, GOODWILL, OR
              INDIRECT, SPECIAL, INCIDENTAL, CONSEQUENTIAL, COVER, BUSINESS INTERRUPTION OR PUNITIVE
              DAMAGES, WHETHER AN ACTION IS IN CONTRACT OR TORT AND REGARDLESS OF THE THEORY OF
              LIABILITY, EVEN IF A PARTY OR ITS AFFILIATES HAVE BEEN ADVISED OF THE POSSIBILITY OF
              SUCH DAMAGES OR IF A PARTY'S OR ITS AFFILIATES' REMEDY OTHERWISE FAILS OF ITS
              ESSENTIAL PURPOSE. THE FOREGOING DISCLAIMER WILL NOT APPLY TO THE EXTENT PROHIBITED BY
              LAW.
            </p>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">
              Termination of These Terms
            </h4>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">For Convenience</h5>

            <p className="mb-4">
              These Terms may be terminated for convenience in the following situations;
            </p>

            <ol className="list-[lower-alpha] ml-8 mb-4">
              <li className="mb-2">
                by the Client any time by clicking the cancellation link on the Web Site, which will
                guide them through the cancellation process when logged in to the Account, or if the
                Client is paying for the Service with a PayPal account, by revoking the billing
                agreement on its PayPal profile; or if the client is paying for the Service via a
                Reseller, by means agreed upon between the Client and the Reseller;
              </li>
              <li className="mb-2">
                by F&B Insights upon decision to end provision of the F&B Insights Services and
                close the Platform; or
              </li>
              <li className="mb-2">
                immediately by either party, if proceedings are initiated for the other party's
                liquidation or insolvency or a negotiated settlement with the other party's
                creditors is concluded or an assignment is made on behalf of the other party for the
                benefit of creditors.
              </li>
            </ol>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">For Default</h5>

            <p className="mb-4">
              These Terms may be terminated for default upon written notice to the other party as
              indicated in the "Notice" Section below:
            </p>

            <ol className="list-[lower-alpha] ml-8 mb-4">
              <li className="mb-2">
                by either party in case of breach of these Terms by the other party, if the breach
                has not been cured within 30 days of receipt of a notice from the non-breaching
                party; or
              </li>
              <li className="mb-2">
                immediately by either party if the other party breaches its obligations, as
                applicable under Sections 12 [Intellectual Property Rights], 15 [Indemnification],
                or 10 [Restrictions] of these Terms.
              </li>
            </ol>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Effect of Termination</h5>

            <p className="mb-4">Upon termination of these Terms,</p>

            <ol className="list-[lower-alpha] ml-8 mb-4">
              <li className="mb-2">
                F&B Insights shall deactivate and permanently delete the Account, within six months
                of the effective date of termination of these Terms. If the Client has specifically
                requested for an earlier deletion of the Account, F&B Insights shall fulfill such
                request within 1 month of its receipt of such request.
              </li>
              <li className="mb-2">
                Client must:
                <ul className="list-disc ml-8 mt-2">
                  <li className="mb-2">
                    stop using and prevent the further usage of the F&B Insights Services,
                    including, without limitation, the Platform;
                  </li>
                  <li className="mb-2">
                    pay any amounts owed to F&B Insights under these Terms; and
                  </li>
                  <li className="mb-2">
                    discharge any liability incurred by the Client before under these Terms prior to
                    their termination; and
                  </li>
                </ul>
              </li>
              <li className="mb-2">
                The following provisions shall survive the termination of these Terms: Sections 1,
                7.4, 10, 11, 12, 14, 15, 16, 18 and 19.
              </li>
            </ol>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Remedies</h5>

            <p className="mb-4">
              If F&B Insights terminates these Terms as a result of an uncured breach by a Client or
              User, F&B Insights is entitled to use the same or similar remedies against any other
              persons who use the F&B Insights Services in conflict with these Terms.
              Notwithstanding the foregoing, F&B Insights may also apply any other remedies
              available to it under the applicable law. Upon application of any remedies, the Client
              or User may lose Access or suffer a loss of certain Features, functions, parts or
              elements of the F&B Insights Services.
            </p>

            <p className="mb-4">
              If F&B Insights has reasonable grounds to believe that the Client's or User's use of
              the F&B Insights Services, including the Account may harm any third persons, F&B
              Insights has the right to take adequate measures under its control to prevent, stop
              and eliminate the harm, where possible, in order to protect those third persons.
            </p>

            <p className="mb-4">
              The F&B Insights has the right to suspend access to all or any part of the Service,
              including removing Content, at any time for violation of this Agreement or to protect
              the integrity, operability, and security of the Service, effective immediately, with
              or without notice. Unless prohibited by law or legal process or to prevent imminent
              harm to the Service or any third party, F&B Insights typically provides notice in the
              form of a banner or email on or before such suspension. F&B Insights will, in its
              discretion and using good faith, tailor any suspension as needed to preserve the
              integrity, operability, and security of the Service.
            </p>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">
              Who You Are Contracting With
            </h4>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">General</h5>

            <p className="mb-4">
              The F&B Insights with whom Client is contracting depends on the domicile of the
              Client. The F&B Insights determines the domicile of the Client based on the country
              indicated in the billing address of the User who concludes these Terms on behalf of
              the Client. During the Free Trial, the F&B Insights determines the domicile of the
              Client based on the location indicated in the IP-address of the User who concludes
              these Terms on behalf of the Client. By accepting these Terms, the Client is
              contracting with the F&B Insights listed opposite such Client's domicile in the
              following table:
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">F&B Insights' contact:</h5>

            <p className="mb-4">
              The United States of America, including all of its territories, and Canada
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">F&B Insights, LLC</h5>

            <p className="mb-4">411 North Lasalle Street, Suite 300, Chicago, IL 60654</p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">
              Governing Law and Jurisdiction
            </h5>

            <p className="mb-4">
              You understand and agree that these Terms of Use constitute the entire agreement
              between you and F&B Insights. The Terms of Use and the relationship between you and
              F&B Insights will be governed by the laws of the State of Florida without regard to
              its conflict of law provisions. The failure of F&B Insights to exercise or enforce any
              provision of or any of its rights under these Terms will not constitute a waiver of
              future enforcement of that or any other provision or right.
            </p>

            <p className="mb-4">
              F&B Insights may assign its rights and duties under these Terms of Use to any party at
              any time without notice to you. You acknowledge that the provisions, disclosures and
              disclaimers set forth above are fair and reasonable and your agreement to follow and
              be bound by them is not the result of fraud, duress or undue influence exercised upon
              you by any person or entity. Both parties agree that there are no representations,
              promises, warranties or undertakings by you or F&B Insights contrary to those set
              above.
            </p>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">General Provisions</h4>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">
              Relationship of the Parties
            </h5>

            <p className="mb-4">
              The parties will act solely as independent contractors. These Terms shall not be
              construed as creating an agency, partnership, joint venture, fiduciary duty, or any
              other form of legal association between the Client and either F&B Insights, and the
              Client shall not represent to the contrary, whether expressly, by implication,
              appearance or otherwise. These Terms are not for the benefit of any third parties.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Severability</h5>

            <p className="mb-4">
              If any term, condition or provision of these Terms is held to be invalid,
              unenforceable or illegal in whole or in part for any reason, that provision shall be
              enforced to the maximum extent permissible so as to affect the intent of the parties.
              The validity and enforceability of the remaining terms, conditions or provisions, or
              portions of them, shall not be affected.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Entire Agreement</h5>

            <p className="mb-4">
              These Terms are the entire agreement between Client and F&B Insights regarding
              Client's use of the F&B Insights Services and supersedes all prior and contemporaneous
              agreements, proposals or representations, written or oral, concerning its subject
              matter. Except as otherwise provided herein, no modification, amendment, or waiver of
              any provision of these Terms will be effective unless in writing and signed by the
              party against whom the modification, amendment or waiver is to be asserted.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Assignment</h5>

            <p className="mb-4">
              Client may not, directly or indirectly, in whole or in part, by operation of law or
              otherwise, assign or transfer these Terms or delegate any of its rights and/or
              obligations under these Terms without F&B Insights' prior written consent. Any
              attempted assignment, transfer or delegation without consent: (1) to any entity
              controlled by, or under common control with the Client, or its permitted successive
              assignees or transferees; or (2) in connection with a merger, reorganization,
              transfer, sale of assets or product lines, or change of control or ownership of the
              Client, or its permitted successive assignees or transferees.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">No Waiver</h5>

            <p className="mb-4">
              Failure of either Party to exercise or enforce any provision of or any of its rights
              under these Terms shall not be deemed a waiver of future enforcement of that or any
              other provision or right.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Notices</h5>

            <p className="mb-4">
              Except as otherwise specified in these Terms, all notices related to these Terms will
              be in writing and will be effective upon (a) personal delivery, (b) the second
              business day after mailing, or (c), except for notices of termination or an
              indemnifiable claim ("Legal Notices"), which shall clearly be identifiable as Legal
              Notices, the day of sending by email. Billing-related notices to you will be addressed
              to the relevant billing contact designated by you. All other notices to you will be
              addressed to the relevant Services system administrator designated by you.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Notice to California Users</h5>

            <p className="mb-4">
              Under California Civil Code Section 1789.3, California Website users are entitled to
              the following specific consumer rights notice: The Complaint Assistance Unit of the
              Division of Consumer Services of the California Department of Consumer Affairs may be
              contacted in writing at 400 R Street, Suite 1080, Sacramento, California 95814, or by
              telephone at (916) 445-1254 or (800) 952-5210.
            </p>

            <p className="mb-6">
              Last update: <strong>April 2nd, 2025</strong>
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
